"use client";

import type { ReactNode } from "react";
import { useEffect, useRef, useState, Suspense } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import type PostHogType from "posthog-js";

import { flushPendingAnalytics } from "@/lib/analytics";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "/ingest";

let posthogInstance: typeof PostHogType | null = null;
let posthogInitialized = false;
const initListeners = new Set<() => void>();

function notifyInitListeners() {
  for (const listener of initListeners) {
    try {
      listener();
    } catch {
      // ignore listener errors
    }
  }
  initListeners.clear();
}

type PosthogProviderProps = {
  children: ReactNode;
};

/**
 * True for exception events that carry no actionable information and only
 * crowd out real issues in Error Tracking.
 *
 * Two known offenders, both verified against production data:
 *
 *  1. Opaque cross-origin script failures. The browser withholds the message,
 *     file and stack, so these arrive as a bare "Script error." with zero
 *     frames — impossible to action, and they mask the real in-app error.
 *  2. "Object Not Found Matching Id:N, MethodName:update, ParamCount:4" —
 *     emitted by Outlook SafeLink / mail-scanner browser automation, not by
 *     our code. This alone accounted for 144 of the ~180 captured exceptions.
 *
 * Anything with a real stack frame is always kept.
 */
function isUndiagnosableNoise(properties: Record<string, unknown> | undefined): boolean {
  if (!properties) return false;

  const list = properties["$exception_list"];
  const first = Array.isArray(list) ? (list[0] as Record<string, unknown> | undefined) : undefined;
  const value = String(first?.value ?? "");

  if (value.includes("Object Not Found Matching Id:")) return true;

  if (value.trim() === "Script error.") {
    const stacktrace = first?.stacktrace as { frames?: unknown[] } | undefined;
    // Keep it if the browser actually gave us frames to work with.
    return !stacktrace?.frames?.length;
  }

  return false;
}

/**
 * True when the page is being driven by browser automation rather than a person.
 *
 * Our own `pnpm test:production` / `test:integration:production` Playwright
 * suites run against https://www.buildforce.io, and every one of those runs was
 * landing in PostHog as a real visitor. Measured over 90 days that was 147
 * sessions — including 30 that started the free health check and "abandoned" it,
 * which made the funnel look like it had a catastrophic first-step drop-off when
 * in fact only 3 humans had ever started it.
 *
 * `navigator.webdriver` is set by Playwright, Puppeteer and Selenium and is the
 * spec-defined signal for exactly this. Suppressing init (rather than filtering
 * later) also keeps automation out of session replay and heatmaps, and off the
 * ingestion bill.
 *
 * Escape hatch: a test that genuinely needs to assert analytics fire can set
 * `window.__BUILDFORCE_ALLOW_ANALYTICS__ = true` before the app boots.
 */
function isAutomatedBrowser(): boolean {
  if (typeof window === "undefined") return false;

  const override = (window as Window & { __BUILDFORCE_ALLOW_ANALYTICS__?: boolean })
    .__BUILDFORCE_ALLOW_ANALYTICS__;
  if (override === true) return false;

  return typeof navigator !== "undefined" && navigator.webdriver === true;
}

async function initPosthog(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (!POSTHOG_KEY) {
    console.warn("[PostHog] No NEXT_PUBLIC_POSTHOG_KEY configured");
    return false;
  }
  if (isAutomatedBrowser()) return false;
  if (posthogInitialized) return true;

  try {
    const posthog = (await import("posthog-js")).default;

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: "https://us.posthog.com",
      // Pageview tracking is handled manually by PosthogPageviewTracker
      // so it can fire on App Router client-side navigations.
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: true,
      persistence: "localStorage+cookie",
      // Heatmaps + recording on every device. Identification is handled by
      // AppSessionProvider effects and inline calls in AuthForm/ShellHeader.
      enable_heatmaps: true,
      // Error Tracking — capture unhandled exceptions and promise rejections.
      // The new central observability sink (replaces Sentry).
      capture_exceptions: true,
      before_send: (event) => {
        if (event?.event === "$exception" && isUndiagnosableNoise(event.properties)) {
          return null;
        }
        return event;
      },
      loaded: () => {
        if (process.env.NODE_ENV === "development") {
          console.log("[PostHog] Initialized successfully");
        }
      },
    });

    posthogInstance = posthog;

    if (typeof window !== "undefined") {
      (window as Window & { posthog?: typeof posthog }).posthog = posthog;
    }

    posthogInitialized = true;
    notifyInitListeners();
    // Events trackEvent() held while posthog-js was still loading.
    flushPendingAnalytics();
    return true;
  } catch (error) {
    console.error("[PostHog] Initialization failed:", error);
    return false;
  }
}

/**
 * Separate component for pageview tracking that uses useSearchParams
 * This allows proper Suspense handling
 */
function PosthogPageviewTracker({ isReady }: { isReady: boolean }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!isReady || !posthogInstance) return;

    const url = window.location.href;
    posthogInstance.capture("$pageview", {
      $current_url: url,
      path: pathname,
      search: searchParams?.toString() ?? "",
    });

    if (process.env.NODE_ENV === "development") {
      console.log("[PostHog] Pageview captured:", pathname);
    }
  }, [pathname, searchParams, isReady]);

  return null;
}

export function PosthogProvider({ children }: PosthogProviderProps) {
  const initCalled = useRef(false);
  const [isReady, setIsReady] = useState(() => posthogInitialized);

  useEffect(() => {
    if (initCalled.current) return;
    initCalled.current = true;

    // If init already happened in another component instance, useState's
    // initializer above already reflects that — nothing more to do.
    if (posthogInitialized) return;

    void initPosthog().then((ok) => {
      if (ok) setIsReady(true);
    });
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <PosthogPageviewTracker isReady={isReady} />
      </Suspense>
      {children}
    </>
  );
}

/**
 * Helper function to manually track events.
 * Pass `sendInstantly: true` for events fired immediately before navigation
 * so the request doesn't get torn down with the page.
 */
export function trackPosthogEvent(
  event: string,
  properties?: Record<string, unknown>,
  options?: { sendInstantly?: boolean },
) {
  if (!posthogInitialized || !posthogInstance) {
    if (typeof window !== "undefined") {
      // Queue the event until init completes — covers very early clicks.
      initListeners.add(() => {
        posthogInstance?.capture(
          event,
          properties,
          options?.sendInstantly ? { send_instantly: true } : undefined,
        );
      });
    }
    return;
  }
  posthogInstance.capture(
    event,
    properties,
    options?.sendInstantly ? { send_instantly: true } : undefined,
  );
}

/**
 * Helper function to identify users.
 * Single source of truth — called from AuthContext on session changes.
 */
export function identifyPosthogUser(userId: string, traits?: Record<string, unknown>) {
  if (!posthogInitialized || !posthogInstance) {
    initListeners.add(() => {
      posthogInstance?.identify(userId, traits);
    });
    return;
  }
  posthogInstance.identify(userId, traits);
}

/**
 * Reset the PostHog distinct_id on sign-out so the next visitor on the same
 * browser doesn't inherit the previous user's identity.
 */
export function resetPosthogUser() {
  if (!posthogInitialized || !posthogInstance) return;
  posthogInstance.reset();
}

/**
 * Associate the current user with an organization (group analytics).
 * Lets you slice product usage by tenant/org in PostHog.
 */
export function setPosthogOrganization(
  organizationId: string,
  traits?: Record<string, unknown>,
) {
  if (!posthogInitialized || !posthogInstance) {
    initListeners.add(() => {
      posthogInstance?.group("organization", organizationId, traits);
    });
    return;
  }
  posthogInstance.group("organization", organizationId, traits);
}
